import { BranchCode } from 'api/risk';
import { useAppSelector } from 'app/hooks';
import { Sizes } from 'components/forms/NumberSelector';
import { NsKey } from 'enums/NsKey';
import { TitleIconKey } from 'enums/titleIconKey';
import { TitleIconFactory } from 'factories/TitleIconFactory';
import { selectBranchCode } from 'features/subscription-process/slices/selectors';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { usePanel } from 'hooks/usePanel';
import { FC, useCallback, useMemo } from 'react';
import { LightContractCard } from '../cards/LightContractCard';
import { MriRisk } from '../risk/MriRisk';
import { LegalProtectionRisk } from '../risk/LegalProtectionRisk';
import { RgiRisk } from '../risk/RgiRisk';
import { UnemploymentRisk } from '../risk/UnemploymentRisk';
import { UpiRisk } from '../risk/UpiRisk';
import { CardState } from '../../types/cardState';
import { useQuoteContractualizationData } from '../../store';
import { useSubscriptionProcess, useSubscriptionProcessActions } from 'features/subscription-process/contexts/SubscriptionProcessContext';
import { ContractualizationResponseDto } from 'api/quote';

interface RiskInformationProps {
  isCardDisabled: boolean;
  status: CardState;
}

const getBranchConfig = (branchCode?: BranchCode) => {
  switch (branchCode) {
    case BranchCode.Unemployment:
      return {
        title: 'CONTRACTUALIZATION.TITLE.UNEMPLOYMENT_RISK',
        cardTitle: 'CONTRACTUALIZATION.CARDS.UNEMPLOYMENT_INFOS',
        size: Sizes.lg
      };
    case BranchCode.UPI:
      return {
        title: 'CONTRACTUALIZATION.TITLE.UPI_RISK',
        cardTitle: 'CONTRACTUALIZATION.CARDS.UPI_INFOS',
        size: Sizes['2xl']
      };
    case BranchCode.RGI:
      return {
        title: 'CONTRACTUALIZATION.TITLE.RGI_RISK',
        cardTitle: 'CONTRACTUALIZATION.CARDS.RGI_INFOS',
        size: Sizes['2xl']
      };
    case BranchCode.Mri:
      return {
        title: 'CONTRACTUALIZATION.TITLE.MRI_RISK',
        cardTitle: 'CONTRACTUALIZATION.CARDS.MRI_INFOS',
        size: Sizes['2xl']
      };
    case BranchCode.LegalProtection:
      return {
        title: 'CONTRACTUALIZATION.TITLE.LEGAL_PROTECTION_RISK',
        cardTitle: 'CONTRACTUALIZATION.CARDS.LEGAL_PROTECTION_INFOS',
        size: Sizes['2xl']
      };
  }
};

export const RiskInformation: FC<RiskInformationProps> = ({
  isCardDisabled,
  status
}) => {
  const { openPanel } = usePanel();
  const { state: subscriptionState } = useSubscriptionProcess();

  const contractualizationData = subscriptionState.contractualizationData as ContractualizationResponseDto;

  const { t } = useDynamicTranslation([NsKey.Contractualization]);

  // branchCode is at the root level of subscriptionState, not inside contractualizationData
  const branchCode = subscriptionState.branchCode;
  const branchConfig = useMemo(() => getBranchConfig(branchCode as BranchCode), [branchCode]);

  const { fetchSubscriptionProcess } = useSubscriptionProcessActions();

  const refresh = useCallback(async () => {
    if (contractualizationData?.riskId)
      await fetchSubscriptionProcess(contractualizationData.riskId, contractualizationData.quoteId);
  }, [fetchSubscriptionProcess, contractualizationData.riskId, contractualizationData.quoteId]);

  // Les composants utilisent le store directement - plus besoin de props
  const riskComponent = useMemo(() => {
    if (!branchCode) return <></>;
    switch (branchCode) {
      case BranchCode.Unemployment:
        return <UnemploymentRisk />;
      case BranchCode.UPI:
        return <UpiRisk />;
      case BranchCode.RGI:
        return <RgiRisk refresh={refresh} />;
      case BranchCode.Mri:
        return <MriRisk />;
      case BranchCode.LegalProtection:
        return <LegalProtectionRisk />;
    }
    return <></>;
  }, [branchCode, refresh]);

  const onSelectRisk = () => {
    if (branchConfig)
      openPanel(
        {
          title: t(branchConfig.title),
          size: branchConfig.size
        },
        riskComponent
      );
  };

  return (
    <LightContractCard
      icon={<TitleIconFactory titleIconKey={TitleIconKey.Home} />}
      title={branchConfig?.cardTitle}
      status={status}
      disabled={isCardDisabled}
      onClick={() => contractualizationData && onSelectRisk()}
    />
  );
};
